import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DialogActions } from 'src/app/model/dictionary';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { CeoDto } from 'src/app/data-transfer/entities/doc-entities/ceo-dto';
import { SigneeDto } from 'src/app/data-transfer/entities/doc-entities/signee-dto';
import { forkJoin, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { PpwrPersonDialogComponent, PpwrPersonDialogData } from '../../dialogs/ppwr-person-dialog/ppwr-person-dialog.component';
import { PpwrProducerDialogComponent, PpwrProducerDialogData } from '../../dialogs/ppwr-producer-dialog/ppwr-producer-dialog.component';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { FriendRequestApiService } from 'src/app/data-transfer/services/friend-request-api-service';

@Component({
  selector: 'app-organization-settings',
  templateUrl: './organization-settings.component.html',
  styleUrls: ['./organization-settings.component.scss'],
})
export class OrganizationSettingsComponent implements OnInit, OnDestroy {
  organization: OrganizationDto = new OrganizationDto();
  manufacturers: CompanyDto[] = [];
  authorizedRepresentatives: CompanyDto[] = [];
  ceos: CeoDto[] = [];
  signees: SigneeDto[] = [];
  organizationCode = '';
  private originalStateSnapshot: string = '';

  organizationForm = new FormGroup({
    address: new FormControl<string | null>(null),
    postCode: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    location: new FormControl<string | null>(null),
  });

  private deletedManufacturerIds: number[] = [];
  private deletedRepresentativeIds: number[] = [];
  private deletedCeoIds: number[] = [];
  private deletedSigneeIds: number[] = [];

  private routeDataSubscription?: Subscription;
  private organizationCodeSubscription?: Subscription;

  isSaving = false;
  allCountries: CountryDto[] = [];

  constructor(
    private dialog: MatDialog,
    private userApiService: UserApiService,
    private friendRequestApiService: FriendRequestApiService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.routeDataSubscription = this.route.data.subscribe(data => {
      this.allCountries = data['allCountries'] ?? [];
      this.setOrganizationData(data['ownOrganization'] as OrganizationDto);
    });

    this.organizationCodeSubscription = this.friendRequestApiService.getInviteCode(true).subscribe({
      next: dto => (this.organizationCode = dto.inviteCode),
    });
  }

  ngOnDestroy(): void {
    this.routeDataSubscription?.unsubscribe();
    this.organizationCodeSubscription?.unsubscribe();
  }

  openCeoDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.managingDirectorLabel'), 'person', false, null, this.ceos, this.deletedCeoIds);
  }

  editCeo(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editCeo'), 'person', false, index, this.ceos, this.deletedCeoIds);
  }

  deleteCeo(index: number): void {
    this.deletePerson(index, this.ceos, this.deletedCeoIds);
  }

  openSigneeDialog(): void {
    this.openPersonDialog(this.translocoService.translate('createDoc.signersDialog.otherSignerTitle'), 'edit', true, null, this.signees, this.deletedSigneeIds);
  }

  editSignee(index: number): void {
    this.openPersonDialog(this.translocoService.translate('dataManagement.organizationSettings.dialogs.editSignee'), 'edit', true, index, this.signees, this.deletedSigneeIds);
  }

  deleteSignee(index: number): void {
    this.deletePerson(index, this.signees, this.deletedSigneeIds);
  }

  openManufacturerDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.producerLabel'),
      'precision_manufacturing',
      null,
      this.manufacturers,
      this.deletedManufacturerIds
    );
  }

  editManufacturer(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editManufacturer'),
      'precision_manufacturing',
      index,
      this.manufacturers,
      this.deletedManufacturerIds
    );
  }

  deleteManufacturer(index: number): void {
    this.deleteProducer(index, this.manufacturers, this.deletedManufacturerIds);
  }

  openAuthorizedRepresentativeDialog(): void {
    this.openProducerDialog(
      this.translocoService.translate('createDoc.createDocDialog.representativeLabel'),
      'assignment_ind',
      null,
      this.authorizedRepresentatives,
      this.deletedRepresentativeIds
    );
  }

  editAuthorizedRepresentative(index: number): void {
    this.openProducerDialog(
      this.translocoService.translate('dataManagement.organizationSettings.dialogs.editRepresentative'),
      'assignment_ind',
      index,
      this.authorizedRepresentatives,
      this.deletedRepresentativeIds
    );
  }

  deleteAuthorizedRepresentative(index: number): void {
    this.deleteProducer(index, this.authorizedRepresentatives, this.deletedRepresentativeIds);
  }

  hasChanges(): boolean {
    return this.createStateSnapshot() !== this.originalStateSnapshot;
  }

  saveChanges(): void {
    if (!this.hasChanges() || this.isSaving) return;

    this.isSaving = true;

    const originalState = JSON.parse(this.originalStateSnapshot || '{}');
    const requests = [
      ...(JSON.stringify(this.organizationForm.getRawValue()) !== JSON.stringify(originalState.formValues)
        ? [this.userApiService.updateOrganizationInfos(this.toCompanyDto())]
        : []),
      ...this.buildChangedCompanyRequests(originalState),
      ...this.buildChangedPersonRequests(originalState),
      ...this.buildDeleteRequests(),
    ];

    if (requests.length === 0) {
      this.isSaving = false;
      this.updateOriginalStateSnapshot();
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.showSnackbar('dataManagement.organizationSettings.messages.saveSuccess', 'app-success', 3000);
        this.isSaving = false;
        this.resetDeletedIds();
        this.updateOriginalStateSnapshot();
      },
      error: error => {
        console.error('Error while saving organization settings:', error);
        this.showSnackbar('dataManagement.organizationSettings.messages.saveError', 'app-error', 4000);
        this.isSaving = false;
      },
    });
  }

  cancelChanges(): void {
    const originalState = JSON.parse(this.originalStateSnapshot || '{}');
    this.manufacturers = structuredClone(originalState.manufacturers ?? []);
    this.authorizedRepresentatives = structuredClone(originalState.authorizedRepresentatives ?? []);
    this.ceos = structuredClone(originalState.ceos ?? []);
    this.signees = structuredClone(originalState.signees ?? []);
    this.patchForm(originalState.formValues);
    this.resetDeletedIds();
  }

  private toCompanyDto(): CompanyDto {
    const value = this.organizationForm.getRawValue();
    return {
      companyProfileId: this.organization.id,
      companyName: this.organization.name,
      address: value.address ?? null,
      postCode: value.postCode ?? null,
      city: value.city ?? null,
      location: value.location ?? null,
    };
  }

  private openPersonDialog<T extends CeoDto>(title: string, icon: string, showFunktion: boolean, index: number | null, list: T[], deletedIds: number[]): void {
    const data = new PpwrPersonDialogData(title, icon, showFunktion, index !== null ? list[index] : undefined);
    this.dialog
      .open(PpwrPersonDialogComponent, this.getPpwrDialogConfig(data, '680px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list[index] = { ...list[index], ...result.data };
        } else {
          list.push({ personProfileId: -1, ...result.data } as T);
        }
      });
  }

  private openProducerDialog(title: string, icon: string, index: number | null, list: CompanyDto[], deletedIds: number[]): void {
    const data = new PpwrProducerDialogData(title, icon, this.allCountries, index !== null ? list[index] : undefined);
    this.dialog
      .open(PpwrProducerDialogComponent, this.getPpwrDialogConfig(data, '720px'))
      .afterClosed()
      .subscribe(result => {
        if (result?.event !== DialogActions.CONFIRM) return;
        if (index !== null) {
          list[index] = { ...list[index], ...result.data };
        } else {
          list.push({ companyProfileId: -1, ...result.data });
        }
      });
  }

  private deletePerson<T extends CeoDto>(index: number, list: T[], deletedIds: number[]): void {
    const item = list[index];
    if (!item) return;
    if (item.personProfileId > 0) deletedIds.push(item.personProfileId);
    list.splice(index, 1);
  }

  private deleteProducer(index: number, list: CompanyDto[], deletedIds: number[]): void {
    const item = list[index];
    if (!item || item.companyProfileId === 0) return;
    if (item.companyProfileId > 0) deletedIds.push(item.companyProfileId);
    list.splice(index, 1);
  }

  private buildChangedCompanyRequests(originalState: any) {
    const requests = [];
    const changedManufacturers = this.getChangedItems(
      this.manufacturers.filter(m => m.companyProfileId !== 0),
      (originalState.manufacturers ?? []).filter((m: CompanyDto) => m.companyProfileId !== 0),
      'companyProfileId'
    );
    const changedRepresentatives = this.getChangedItems(this.authorizedRepresentatives, originalState.authorizedRepresentatives ?? [], 'companyProfileId');
    if (changedManufacturers.length > 0) requests.push(this.userApiService.createOrUpdateManufacturerProfiles(changedManufacturers));
    if (changedRepresentatives.length > 0) requests.push(this.userApiService.createOrUpdateRepresentativeProfiles(changedRepresentatives));
    return requests;
  }

  private buildChangedPersonRequests(originalState: any) {
    const requests = [];
    const changedCeos = this.getChangedItems(this.ceos, originalState.ceos ?? [], 'personProfileId');
    const changedSignees = this.getChangedItems(this.signees, originalState.signees ?? [], 'personProfileId');
    if (changedCeos.length > 0) requests.push(this.userApiService.createOrUpdateCeoProfiles(changedCeos));
    if (changedSignees.length > 0) requests.push(this.userApiService.createOrUpdateSigneeProfiles(changedSignees));
    return requests;
  }

  private buildDeleteRequests() {
    const requests = [];
    if (this.deletedManufacturerIds.length > 0) requests.push(this.userApiService.deleteManufacturerProfiles(this.deletedManufacturerIds));
    if (this.deletedRepresentativeIds.length > 0) requests.push(this.userApiService.deleteRepresentativeProfiles(this.deletedRepresentativeIds));
    if (this.deletedCeoIds.length > 0) requests.push(this.userApiService.deleteCeoProfiles(this.deletedCeoIds));
    if (this.deletedSigneeIds.length > 0) requests.push(this.userApiService.deleteSigneeProfiles(this.deletedSigneeIds));
    return requests;
  }

  private getChangedItems<T>(current: T[], original: T[], idKey: keyof T): T[] {
    return current.filter(item => {
      if ((item[idKey] as number) < 0) return true;
      const originalItem = original.find((o: T) => o[idKey] === item[idKey]);
      return !originalItem || JSON.stringify(item) !== JSON.stringify(originalItem);
    });
  }

  private setOrganizationData(result: OrganizationDto): void {
    this.organization = result;
    this.manufacturers = result.manufacturerProfiles ?? [];
    this.authorizedRepresentatives = result.authorizedRepresentativeProfiles ?? [];
    this.ceos = result.ceoProfiles ?? [];
    this.signees = result.signeeProfiles ?? [];
    this.patchForm(result);
    this.updateOriginalStateSnapshot();
    this.resetDeletedIds();
  }

  private patchForm(values: { address?: string | null; postCode?: string | null; city?: string | null; location?: string | null }): void {
    this.organizationForm.patchValue({
      address: values.address ?? null,
      postCode: values.postCode ?? null,
      city: values.city ?? null,
      location: values.location ?? null,
    });
  }

  private updateOriginalStateSnapshot(): void {
    this.originalStateSnapshot = this.createStateSnapshot();
  }

  private createStateSnapshot(): string {
    return JSON.stringify({
      formValues: this.organizationForm.getRawValue(),
      manufacturers: this.manufacturers,
      authorizedRepresentatives: this.authorizedRepresentatives,
      ceos: this.ceos,
      signees: this.signees,
    });
  }

  private showSnackbar(messageKey: string, panelClass: string, duration: number): void {
    this.snackBar.open(this.translocoService.translate(messageKey), this.translocoService.translate('common.buttons.close'), {
      duration,
      panelClass: ['app-snackbar', panelClass],
    });
  }

  private getPpwrDialogConfig(data: unknown, width: string): MatDialogConfig {
    const config = getDialogConfig(data, width);
    config.maxWidth = 'calc(100vw - 32px)';
    config.panelClass = 'ppwr-dialog-panel';
    config.restoreFocus = true;
    return config;
  }

  private resetDeletedIds(): void {
    this.deletedManufacturerIds = [];
    this.deletedRepresentativeIds = [];
    this.deletedCeoIds = [];
    this.deletedSigneeIds = [];
  }
}
